import { getDemoHomePath } from "./demoUtils";
import { readRemoteSession, isWebSession, clearRemoteSession } from "../context/RemoteLaunchContext";

/**
 * Only ever leave the app for a URL an admin typed into the venue record, and
 * only over http(s). Belt-and-braces against a `javascript:` or `data:` URL
 * reaching window.location — the value cannot come from the request, but this
 * is the one place it becomes executable, so it is checked here too.
 */
function safeReturnUrl(url){
    if (!url) return null;
    try {
        var parsed = new URL(url, window.location.origin);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
        return parsed.href;
    } catch (e) {
        return null;
    }
}

/**
 * Centralized lobby-return helper. Branches between kiosk (back to /home) and
 * remote launch (postMessage to parent + safety screen).
 *
 * See docs/remote-game-launch-plan.md §5.4 and §5.4.1 for the message contract.
 *
 * @param {Object} args
 * @param {Object} args.remoteCtx           value from RemoteLaunchContext
 * @param {function} args.setRedirect       called with {path, state}
 * @param {string} args.result              one of: completed | denied_daily_limit |
 *                                          no_promotion | email_required | cancelled | error
 * @param {Object} [args.player]            { email?, phone? } — never include other PII fields
 * @param {Object} [args.ticket]            { id, promo_id } — present only when result === "completed"
 */
export function exitGame({ remoteCtx, setRedirect, result, player, ticket }) {
    var isRemote = remoteCtx && remoteCtx.isRemote;

    // Standalone web (/go/<slug>): there is no parent frame to signal, and no
    // terminal screen either. Whatever ended the session has already told the
    // player why — "visit the Players Club desk", "you already played today",
    // the receipt itself — all in a modal they had to dismiss to get here. A
    // page that repeats it is pure friction, so web sessions never reach
    // /launch-complete at all. That route now exists only for the iframe flow,
    // where it is a genuine safety net against a parent that never removes the
    // frame.
    var webSession = isRemote ? readRemoteSession() : null;
    if (isRemote && isWebSession(webSession)) {
        // More promotions still open — back to the lobby to pick the next one.
        // Nothing has ended yet.
        if ((result || "completed") === "completed" && remotePromoCount(remoteCtx) > 1) {
            setRedirect({ path: getDemoHomePath(), state: null });
            return;
        }

        // Done. Every outcome leaves the same way, because the reason was
        // already delivered and the exit does not depend on it.
        var target = safeReturnUrl(
            webSession.displayOptions && webSession.displayOptions.returnUrl
        );
        if (target) {
            // Leaving the app entirely, so do the cleanup /launch-complete
            // used to do on mount.
            clearRemoteSession();
            try { sessionStorage.removeItem("auth"); } catch (e) { /* ignore */ }
            // replace(), not href: the back button must not land on a dead
            // session pretending to still be playable.
            window.location.replace(target);
            return;
        }

        // No return URL configured. The lobby is the least-bad exit — it is
        // never a dead end, since it reports its own state ("no promotions",
        // "come back tomorrow") on arrival.
        setRedirect({ path: getDemoHomePath(), state: null });
        return;
    }

    if (isRemote) {
        // A finished game at a venue running more than one promotion sends the
        // player back to the lobby to pick another — the frame stays open and
        // the parent is not told anything. Only when there is nothing else to
        // play does the frame close and report to the parent. Non-completion
        // exits (cancel, error, daily limit, no promotion) always close: the
        // player is leaving, not choosing again.
        if ((result || "completed") === "completed" && remotePromoCount(remoteCtx) > 1) {
            setRedirect({ path: getDemoHomePath(), state: null });
            return;
        }

        var msg = {
            type: "pt:gameComplete",
            result: result || "completed",
            at: Date.now(),
        };
        if (player) {
            msg.player = {};
            if (player.email) msg.player.email = player.email;
            if (player.phone) msg.player.phone = player.phone;
            msg.player.registered_this_session = !!remoteCtx.registeredThisSession;
        }
        if (ticket) msg.ticket = ticket;

        try {
            // M1: "*" when parentOrigin unknown. M2 enforces parentOrigin strictly.
            window.parent.postMessage(msg, remoteCtx.parentOrigin || "*");
        } catch (e) { /* parent may be gone */ }

        setRedirect({ path: "/launch-complete", state: null });
        return;
    }

    // Existing kiosk behavior
    setRedirect({ path: getDemoHomePath(), state: null });
}

/**
 * How many promotions this venue is currently running, as recorded by Home when
 * it loaded the lobby. Read from the stored session rather than the caller's
 * remoteCtx so every game page gets this for free — the four game components
 * each hand-build their remoteCtx and would otherwise have to remember to
 * include it. An explicit remoteCtx.promoCount still wins if present.
 *
 * Unknown (0/undefined) deliberately behaves like "one promotion": close the
 * frame. Stranding the player in a lobby the parent isn't expecting is worse
 * than closing a frame that could have stayed open.
 */
function remotePromoCount(remoteCtx){
    if (remoteCtx && remoteCtx.promoCount != null) return remoteCtx.promoCount;
    var stored = readRemoteSession() || {};
    return stored.promoCount || 0;
}

/**
 * Build a minimal player payload from a Player model object for use with exitGame().
 * Returns null if no useful fields are present.
 */
export function playerEcho(player){
    if (!player) return null;
    var out = {};
    if (player.email) out.email = player.email;
    if (player.phone) out.phone = player.phone;
    return Object.keys(out).length ? out : null;
}
